import { useMemo, useState } from "react";
import { toast } from "sonner";
import { api, inr, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CalendarPlus, X } from "lucide-react";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export function BulkScheduleDialog({ trips, onDone }) {
  const [open, setOpen] = useState(false);
  const [tripId, setTripId] = useState("");
  const [picked, setPicked] = useState([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [days, setDays] = useState([4, 5]);
  const [everyN, setEveryN] = useState("");
  const [seats, setSeats] = useState(20);
  const [override, setOverride] = useState(false);
  const [prices, setPrices] = useState({ quad: "", triple: "", double: "" });
  const [saving, setSaving] = useState(false);
  const trip = trips.find((t) => t.id === tripId);

  const generated = useMemo(() => {
    if (!from || !to) return [];
    const out = [];
    const start = new Date(from), end = new Date(to);
    if (Number.isNaN(start) || Number.isNaN(end) || end < start) return [];
    const n = Number(everyN);
    for (let d = new Date(start), i = 0; d <= end && out.length < 200; d.setDate(d.getDate() + 1), i++) {
      const wd = (d.getDay() + 6) % 7;
      if (n > 0 ? i % n === 0 : days.includes(wd)) out.push(iso(d));
    }
    return out;
  }, [from, to, days, everyN]);

  const dates = useMemo(() => [...new Set([...picked.map(iso), ...generated])].sort(), [picked, generated]);

  const submit = async () => {
    if (!tripId) { toast.error("Select a trip"); return; }
    if (!dates.length) { toast.error("Pick at least one date"); return; }
    let p = null;
    if (override) {
      p = { quad: Number(prices.quad), triple: Number(prices.triple), double: Number(prices.double) };
      if (!(p.quad > 0 && p.triple > 0 && p.double > 0)) { toast.error("Enter all three sharing prices or turn off override"); return; }
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/departures/bulk", { trip_id: tripId, dates, prices: p, total_seats: Number(seats) || 0, pickup_point: trip?.pickup_points?.[0] || "" });
      toast.success(`${data.created} departure(s) scheduled${data.skipped.length ? ` · ${data.skipped.length} already existed` : ""}`);
      setOpen(false); setPicked([]); setFrom(""); setTo(""); onDone?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button variant="outline" className="rounded-full" onClick={() => setOpen(true)} data-testid="bulk-schedule"><CalendarPlus className="h-4 w-4 mr-1" /> Bulk Schedule</Button>
      <DialogContent className="max-w-2xl max-h-[92dvh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-2xl">Schedule many departures at once</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Trip</Label>
            <Select value={tripId} onValueChange={setTripId}>
              <SelectTrigger data-testid="bulk-trip"><SelectValue placeholder="Select trip" /></SelectTrigger>
              <SelectContent>{trips.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <Tabs defaultValue="pick">
            <TabsList className="grid grid-cols-2"><TabsTrigger value="pick" data-testid="bulk-tab-pick">Pick dates</TabsTrigger><TabsTrigger value="repeat" data-testid="bulk-tab-repeat">Repeat pattern</TabsTrigger></TabsList>
            <TabsContent value="pick" className="pt-2">
              <p className="text-xs text-muted-foreground mb-2">Tap every date you want. Switch months with the arrows.</p>
              <div className="flex justify-center rounded-xl border p-2"><Calendar mode="multiple" selected={picked} onSelect={(v) => setPicked(v || [])} disabled={{ before: new Date() }} numberOfMonths={1} data-testid="bulk-calendar" /></div>
            </TabsContent>
            <TabsContent value="repeat" className="pt-2 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="bulk-from" /></div>
                <div><Label>To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="bulk-to" /></div>
              </div>
              <div>
                <Label>On these weekdays</Label>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {WEEKDAYS.map((w, i) => (
                    <button key={w} type="button" disabled={Number(everyN) > 0} onClick={() => setDays(days.includes(i) ? days.filter((x) => x !== i) : [...days, i])} data-testid={`bulk-day-${w.toLowerCase()}`}
                      className={`rounded-full border px-3 py-1.5 text-xs transition-colors disabled:opacity-40 ${days.includes(i) ? "bg-primary text-primary-foreground border-primary" : "hover:bg-muted"}`}>{w}</button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm"><span className="text-muted-foreground">or every</span><Input type="number" min="0" value={everyN} onChange={(e) => setEveryN(e.target.value)} placeholder="N" className="w-20" data-testid="bulk-every" /><span className="text-muted-foreground">days from the start date</span></div>
            </TabsContent>
          </Tabs>

          <div className="rounded-xl bg-muted/50 p-3">
            <div className="flex items-center justify-between"><p className="text-sm font-medium" data-testid="bulk-count">{dates.length} date{dates.length === 1 ? "" : "s"} selected</p>{dates.length > 0 && <button type="button" className="text-xs text-muted-foreground hover:text-destructive" onClick={() => { setPicked([]); setFrom(""); setTo(""); }}>Clear</button>}</div>
            <div className="mt-2 flex flex-wrap gap-1.5 max-h-24 overflow-auto">
              {dates.map((d) => <span key={d} className="inline-flex items-center gap-1 rounded-full bg-card border px-2 py-0.5 text-[11px] font-mono" data-testid="bulk-date-chip">{d}{picked.some((p) => iso(p) === d) && <button type="button" onClick={() => setPicked(picked.filter((p) => iso(p) !== d))}><X className="h-3 w-3" /></button>}</span>)}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Total seats per date (0 = unlimited)</Label><Input type="number" min="0" value={seats} onChange={(e) => setSeats(e.target.value)} data-testid="bulk-seats" /></div>
            <div className="rounded-xl border p-3 space-y-2">
              <label className="flex items-center gap-2 text-sm"><Switch checked={override} onCheckedChange={setOverride} data-testid="bulk-override" /> Override trip prices</label>
              {override ? (
                <div className="grid grid-cols-3 gap-2">
                  {["quad", "triple", "double"].map((k) => <Input key={k} type="number" min="1" placeholder={k} value={prices[k]} onChange={(e) => setPrices({ ...prices, [k]: e.target.value })} data-testid={`bulk-price-${k}`} />)}
                </div>
              ) : <p className="text-xs text-muted-foreground">{trip?.prices ? `Q ${inr(trip.prices.quad)} · T ${inr(trip.prices.triple)} · D ${inr(trip.prices.double)}` : "Uses each trip's default prices"}</p>}
            </div>
          </div>

          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} disabled={saving} data-testid="bulk-submit">{saving ? "Scheduling…" : `Schedule ${dates.length || ""} departure${dates.length === 1 ? "" : "s"}`}</Button></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
